import axios from 'axios';
import { all, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import { CHANNELS, uploadMediaAssets } from 'app/api-routes';
import { TABLE_NAMES } from 'app/types/constants';
import { MediaAssetTypes } from 'ducks/mediaAssets/types';
import { ChannelsScope, MessageTypes, PaginationDirection } from './types';
import {
  channelsFailure,
  channelsRequest,
  channelsSuccess,
  channelInfoRequest,
  channelInfoSuccess,
  channelInfoFailure,
  refreshChannels,
  channelAdded,
  newMessageReceived,
  messagesRequest,
  messagesSuccess,
  messagesFailure,
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  markAsReadRequest,
  markAsReadSuccess,
  markAsReadFailure,
  editMessageRequest,
  editMessageSuccess,
  editMessageFailure,
  deleteMessageRequest,
  deleteMessageSuccess,
  deleteMessageFailure,
  searchMessagesRequest,
  searchMessagesSuccess,
  searchMessagesFailure,
} from './actions';

export function* channelsSaga({ payload: request }) {
  try {
    const params = request?.unreadOnly ? { unreadOnly: true } : undefined;
    const { payload, warnings } = yield axios.get(CHANNELS, { params });
    yield put(channelsSuccess(payload, { warnings }));
  } catch (err) {
    yield put(channelsFailure(err));
  }
}

export function* refreshChannelsSaga() {
  const scope = yield select((state) => state.chat?.channelsScope);
  yield put(channelsRequest({ unreadOnly: scope !== ChannelsScope.ALL }));
}

// Channels being fetched because a message arrived for one that is not in the list yet.
const pendingChannelIDs = new Set();

export function* unknownChannelSaga({ payload: message }) {
  const channelID = message?.channelID;
  if (channelID == null || pendingChannelIDs.has(channelID)) return;

  const { channels, channelsScope } = yield select((state) => state.chat);
  if (channels.some((channel) => channel.channelID === channelID)) return;

  // An unread-only list only gains channels through messages that make them unread.
  if (channelsScope !== ChannelsScope.ALL) {
    const userID = yield select((state) => state.auth?.userInfo?.userID);
    if (message.type === MessageTypes.ACTIVITY || message.userCreated === userID) return;
  }

  pendingChannelIDs.add(channelID);
  try {
    const { payload } = yield axios.get(`${CHANNELS}/${channelID}`);
    // The server's unread count already includes this message.
    yield put(channelAdded({ ...payload, lastMessage: message }));
  } catch (err) {
    // No access to the channel, or it is gone: leave the list as it is.
  } finally {
    pendingChannelIDs.delete(channelID);
  }
}

function* channelInfoSaga({ payload: { channelID } }) {
  try {
    const { payload, warnings } = yield axios.get(`${CHANNELS}/${channelID}`);
    yield put(channelInfoSuccess(payload, { warnings }));
  } catch (err) {
    yield put(channelInfoFailure(err));
  }
}

function* messagesSaga({ payload }) {
  const { channelID, beforeMessageID, afterMessageID, aroundMessageID, limit } = payload;

  const params = {
    ...(limit && { limit }),
    ...(beforeMessageID && { beforeMessageID }),
    ...(afterMessageID != null && { afterMessageID }),
    ...(aroundMessageID && { aroundMessageID }),
  };

  try {
    const { payload: data, warnings } = yield axios.get(`${CHANNELS}/${channelID}/messages`, { params });

    let direction = PaginationDirection.INITIAL;
    if (beforeMessageID) direction = PaginationDirection.BEFORE;
    else if (afterMessageID != null) direction = PaginationDirection.AFTER;

    yield put(messagesSuccess({ ...data, channelID, direction }, { warnings }));
  } catch (err) {
    yield put(messagesFailure(err));
  }
}

function* sendMessageSaga({ payload: { channelID, content, file } }) {
  try {
    let mediaAssetID = null;

    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      const { payload: uploadedID } = yield axios.post(uploadMediaAssets, formData, {
        params: { entityID: channelID, tableName: TABLE_NAMES.CHANNELS, type: MediaAssetTypes.PHOTO },
      });
      mediaAssetID = uploadedID;
    }

    const body = {
      ...(content && { content }),
      ...(mediaAssetID && { mediaAssetID }),
    };

    const { payload: data, warnings } = yield axios.post(`${CHANNELS}/${channelID}/messages`, { payload: body });
    yield put(sendMessageSuccess(data, { warnings }));
    if (data.messageID) {
      yield put(markAsReadRequest({ channelID, messageID: data.messageID }));
    }
  } catch (err) {
    yield put(sendMessageFailure({ err, channelID }));
  }
}

function* markAsReadSaga({ payload: { channelID, messageID } }) {
  try {
    const channel = yield select((state) =>
      (state.chat?.channels || []).find((c) => c.channelID === channelID)
    );
    yield axios.post(`${CHANNELS}/${channelID}/read`, { payload: { messageID } });
    yield put(markAsReadSuccess({ channelID, messageID }));
    if (channel && channel.unreadCount > 0) {
      yield put(channelInfoRequest({ channelID }));
    }
  } catch (err) {
    yield put(markAsReadFailure(err));
  }
}

function* editMessageSaga({ payload: { channelID, messageID, content } }) {
  try {
    const { payload: data, warnings } = yield axios.put(
      `${CHANNELS}/${channelID}/messages/${messageID}`,
      { payload: { content } }
    );
    yield put(editMessageSuccess(data, { warnings }));
  } catch (err) {
    yield put(editMessageFailure(err));
  }
}

function* deleteMessageSaga({ payload: { channelID, messageID } }) {
  try {
    const { warnings } = yield axios.delete(`${CHANNELS}/${channelID}/messages/${messageID}`);
    yield put(deleteMessageSuccess({ channelID, messageID }, { warnings }));
  } catch (err) {
    yield put(deleteMessageFailure(err));
  }
}

function* searchMessagesSaga({ payload: { channelID, query } }) {
  try {
    const { payload: data, warnings } = yield axios.get(
      `${CHANNELS}/${channelID}/messages/search`,
      { params: { query } }
    );
    yield put(searchMessagesSuccess({ channelID, query, results: data?.results || [] }, { warnings }));
  } catch (err) {
    yield put(searchMessagesFailure(err));
  }
}

export default function* () {
  yield all([
    takeLatest(channelsRequest, channelsSaga),
    takeLatest(refreshChannels, refreshChannelsSaga),
    takeEvery(newMessageReceived, unknownChannelSaga),
    takeLatest(channelInfoRequest, channelInfoSaga),
    takeLatest(messagesRequest, messagesSaga),
    takeEvery(sendMessageRequest, sendMessageSaga),
    takeLatest(markAsReadRequest, markAsReadSaga),
    takeEvery(editMessageRequest, editMessageSaga),
    takeEvery(deleteMessageRequest, deleteMessageSaga),
    takeLatest(searchMessagesRequest, searchMessagesSaga),
  ]);
}
